import { CHANGE_TYPES, type ChangeType } from "./enums";
import { parseOneOf } from "./guards";
import { buildTextMarkup, normalizeAmendmentMarkupLayout } from "./markupDoc";
import { err, ok, type Result } from "./result";
import { cleanNullableText } from "./text";
import { parseDateInputAtUtcHour } from "./time";
import type { RichTextDoc } from "./richText";

export type TextDiffUpdateDraft<LegislationId extends string = string, SectionId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly sectionId: SectionId;
  readonly amendingLegislation: string;
  readonly commencementDate: number;
  readonly changeType: ChangeType;
  readonly summary?: string | null;
  readonly previousText: string;
  readonly amendedText: string;
};

export type PreparedTextDiffUpdate<LegislationId extends string = string, SectionId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly appliesToSectionIds: readonly [SectionId];
  readonly amendingLegislation: string;
  readonly commencementDate: number;
  readonly changeType: ChangeType;
  readonly summary: string | null;
  readonly markup: RichTextDoc;
};

export type UpdateLogInsertDraft<LegislationId extends string = string, SectionId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly appliesToSectionIds: readonly SectionId[];
  readonly amendingLegislation: string;
  readonly commencementDate: number;
  readonly changeType: ChangeType;
  readonly summary?: string | null;
  readonly markup?: RichTextDoc | null;
  readonly now: number;
};

export type UpdateLogFormDraft<LegislationId extends string = string, SectionId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly selectedSectionId: SectionId | null;
  readonly amendingLegislation: string;
  readonly commencementDate: string;
  readonly changeType: ChangeType;
  readonly summary?: string;
  readonly markup?: RichTextDoc | null;
};

export type UpdateLogFormState = {
  readonly amendingLegislation: string;
  readonly commencementDate: string;
  readonly changeType: ChangeType;
};

export const emptyUpdateLogFormState = (): UpdateLogFormState => ({
  amendingLegislation: "",
  commencementDate: "",
  changeType: "amended",
});

export type UpdateLogFormRequiredTextDraft = Pick<
  UpdateLogFormDraft,
  "amendingLegislation" | "commencementDate"
>;

export const hasUpdateLogFormRequiredText = (draft: UpdateLogFormRequiredTextDraft): boolean =>
  draft.amendingLegislation.trim() !== "" && draft.commencementDate.trim() !== "";

export const updateLogChangeTypeFromInput = (value: string, fallback: ChangeType): ChangeType => {
  const parsed = parseOneOf(CHANGE_TYPES, value);
  return parsed.ok ? parsed.value : fallback;
};

export type UpdateLogFormAction =
  | { readonly type: "amendingLegislationInput"; readonly value: string }
  | { readonly type: "commencementDateInput"; readonly value: string }
  | { readonly type: "changeTypeInput"; readonly value: string };

export const nextUpdateLogFormState = (
  current: UpdateLogFormState,
  action: UpdateLogFormAction,
): UpdateLogFormState => {
  switch (action.type) {
    case "amendingLegislationInput":
      return { ...current, amendingLegislation: action.value };
    case "commencementDateInput":
      return { ...current, commencementDate: action.value };
    case "changeTypeInput":
      return { ...current, changeType: updateLogChangeTypeFromInput(action.value, current.changeType) };
  }
};

export const updateLogFormSubmittedState = (
  current: UpdateLogFormState,
): UpdateLogFormState => ({
  ...current,
  amendingLegislation: "",
  commencementDate: "",
});

export type UpdateLogSubmissionState =
  | { readonly kind: "idle"; readonly error: null }
  | { readonly kind: "submitting"; readonly error: null }
  | { readonly kind: "failed"; readonly error: string };

export const emptyUpdateLogSubmissionState = (): UpdateLogSubmissionState => ({
  kind: "idle",
  error: null,
});

export type UpdateLogSubmissionAction =
  | { readonly type: "cleared" }
  | { readonly type: "started" }
  | { readonly type: "succeeded" }
  | { readonly type: "failed"; readonly error: string };

export const nextUpdateLogSubmissionState = (
  _current: UpdateLogSubmissionState,
  action: UpdateLogSubmissionAction,
): UpdateLogSubmissionState => {
  switch (action.type) {
    case "cleared":
    case "succeeded":
      return emptyUpdateLogSubmissionState();
    case "started":
      return { kind: "submitting", error: null };
    case "failed":
      return { kind: "failed", error: action.error };
  }
};

export const updateLogSubmitButtonLabel = (
  state: UpdateLogSubmissionState,
): "Add log entry" | "Adding..." => state.kind === "submitting" ? "Adding..." : "Add log entry";

export const updateLogSubmissionError = (state: UpdateLogSubmissionState): string | null =>
  state.kind === "failed" ? state.error : null;

export type PreparedUpdateLogFormDraft<LegislationId extends string = string, SectionId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly appliesToSectionIds: SectionId[];
  readonly amendingLegislation: string;
  readonly commencementDate: number;
  readonly changeType: ChangeType;
  readonly summary?: string;
  readonly markup?: RichTextDoc;
};

export type PreparedUpdateLogInsert<LegislationId extends string = string, SectionId extends string = string> = {
  readonly legislationId: LegislationId;
  readonly appliesToSectionIds: SectionId[];
  readonly amendingLegislation: string;
  readonly commencementDate: number;
  readonly changeType: ChangeType;
  readonly summary: string | null;
  readonly markup: RichTextDoc | null;
  readonly createdAt: number;
  readonly emailedAt: null;
};

export type UpdateLogEmailedPatch = {
  readonly emailedAt: number;
};

export type UpdateLogInsertError =
  | { readonly kind: "missingAmendingLegislation" }
  | { readonly kind: "invalidCommencementDate" };

export type UpdateLogFormError =
  | UpdateLogInsertError
  | { readonly kind: "missingCommencementDate" };

export type TextDiffUpdateError =
  | UpdateLogInsertError
  | { readonly kind: "emptyDiff" };

export const updateLogInsertErrorMessage = (error: UpdateLogInsertError): string => {
  switch (error.kind) {
    case "missingAmendingLegislation":
      return "Amending legislation is required";
    case "invalidCommencementDate":
      return "Commencement date must be a finite timestamp";
  }
};

export const updateLogFormErrorMessage = (error: UpdateLogFormError): string => {
  switch (error.kind) {
    case "missingCommencementDate":
      return "Commencement date is required";
    default:
      return updateLogInsertErrorMessage(error);
  }
};

export const textDiffUpdateErrorMessage = (error: TextDiffUpdateError): string => {
  switch (error.kind) {
    case "emptyDiff":
      return "No word-level differences found";
    default:
      return updateLogInsertErrorMessage(error);
  }
};

type UpdateLogCoreInput = Pick<UpdateLogInsertDraft, "amendingLegislation" | "commencementDate" | "summary">;

type PreparedUpdateLogCoreFields = {
  readonly amendingLegislation: string;
  readonly commencementDate: number;
  readonly summary: string | null;
};

const prepareUpdateLogCoreFields = (
  draft: UpdateLogCoreInput,
): PreparedUpdateLogCoreFields => ({
  amendingLegislation: draft.amendingLegislation.trim(),
  commencementDate: draft.commencementDate,
  summary: cleanNullableText(draft.summary),
});

const prepareUpdateLogCoreFieldsResult = (
  draft: UpdateLogCoreInput,
): Result<PreparedUpdateLogCoreFields, UpdateLogInsertError> => {
  const fields = prepareUpdateLogCoreFields(draft);
  if (fields.amendingLegislation === "") return err({ kind: "missingAmendingLegislation" });
  if (!Number.isFinite(fields.commencementDate)) return err({ kind: "invalidCommencementDate" });
  return ok(fields);
};

const normalizeNullableAmendmentMarkup = (markup: RichTextDoc | null | undefined): RichTextDoc | null =>
  markup == null ? null : normalizeAmendmentMarkupLayout(markup);

export const prepareUpdateLogInsert = <LegislationId extends string, SectionId extends string>(
  draft: UpdateLogInsertDraft<LegislationId, SectionId>,
): PreparedUpdateLogInsert<LegislationId, SectionId> => {
  const fields = prepareUpdateLogCoreFields(draft);
  return {
    legislationId: draft.legislationId,
    appliesToSectionIds: [...draft.appliesToSectionIds],
    amendingLegislation: fields.amendingLegislation,
    commencementDate: fields.commencementDate,
    changeType: draft.changeType,
    summary: fields.summary,
    markup: normalizeNullableAmendmentMarkup(draft.markup),
    createdAt: draft.now,
    emailedAt: null,
  };
};

export const prepareUpdateLogInsertResult = <LegislationId extends string, SectionId extends string>(
  draft: UpdateLogInsertDraft<LegislationId, SectionId>,
): Result<PreparedUpdateLogInsert<LegislationId, SectionId>, UpdateLogInsertError> => {
  const fields = prepareUpdateLogCoreFieldsResult(draft);
  if (!fields.ok) return fields;

  return ok({
    legislationId: draft.legislationId,
    appliesToSectionIds: [...draft.appliesToSectionIds],
    amendingLegislation: fields.value.amendingLegislation,
    commencementDate: fields.value.commencementDate,
    changeType: draft.changeType,
    summary: fields.value.summary,
    markup: normalizeNullableAmendmentMarkup(draft.markup),
    createdAt: draft.now,
    emailedAt: null,
  });
};

const parseRequiredInputDate = (
  value: string,
): Result<number, UpdateLogFormError> => {
  const cleaned = value.trim();
  if (cleaned === "") return err({ kind: "missingCommencementDate" });

  const parsed = parseDateInputAtUtcHour(cleaned, 0);
  return parsed.ok ? ok(parsed.value) : err({ kind: "invalidCommencementDate" });
};

export const prepareUpdateLogFormDraft = <LegislationId extends string, SectionId extends string>(
  draft: UpdateLogFormDraft<LegislationId, SectionId>,
): Result<PreparedUpdateLogFormDraft<LegislationId, SectionId>, UpdateLogFormError> => {
  const amendingLegislation = draft.amendingLegislation.trim();
  if (amendingLegislation === "") return err({ kind: "missingAmendingLegislation" });

  const commencementDate = parseRequiredInputDate(draft.commencementDate);
  if (!commencementDate.ok) return commencementDate;

  const summary = cleanNullableText(draft.summary);
  const markup = normalizeNullableAmendmentMarkup(draft.markup);

  return ok({
    legislationId: draft.legislationId,
    appliesToSectionIds: draft.selectedSectionId === null ? [] : [draft.selectedSectionId],
    amendingLegislation,
    commencementDate: commencementDate.value,
    changeType: draft.changeType,
    ...(summary === null ? {} : { summary }),
    ...(markup === null ? {} : { markup }),
  });
};

export type UpdateLogFormView<LegislationId extends string = string, SectionId extends string = string> = {
  readonly prepared: Result<PreparedUpdateLogFormDraft<LegislationId, SectionId>, UpdateLogFormError>;
  readonly hasRequiredText: boolean;
  readonly canSubmit: boolean;
  readonly errorMessage: string | null;
  readonly buttonLabel: "Add log entry";
};

export const updateLogFormView = <LegislationId extends string, SectionId extends string>(
  draft: UpdateLogFormDraft<LegislationId, SectionId>,
): UpdateLogFormView<LegislationId, SectionId> => {
  const prepared = prepareUpdateLogFormDraft(draft);
  const hasRequiredText = hasUpdateLogFormRequiredText(draft);
  return {
    prepared,
    hasRequiredText,
    canSubmit: hasRequiredText,
    errorMessage: prepared.ok ? null : updateLogFormErrorMessage(prepared.error),
    buttonLabel: "Add log entry",
  };
};

export const updateLogEmailedPatch = (now: number): UpdateLogEmailedPatch => ({
  emailedAt: now,
});

export const prepareTextDiffUpdate = <LegislationId extends string, SectionId extends string>(
  draft: TextDiffUpdateDraft<LegislationId, SectionId>,
): Result<PreparedTextDiffUpdate<LegislationId, SectionId>, TextDiffUpdateError> => {
  const fields = prepareUpdateLogCoreFieldsResult(draft);
  if (!fields.ok) return fields;

  const markup = buildTextMarkup(draft.previousText, draft.amendedText);
  if (!markup.hasChanges) return err({ kind: "emptyDiff" });

  return ok({
    legislationId: draft.legislationId,
    appliesToSectionIds: [draft.sectionId] as const,
    amendingLegislation: fields.value.amendingLegislation,
    commencementDate: fields.value.commencementDate,
    changeType: draft.changeType,
    summary: fields.value.summary,
    markup: markup.doc,
  });
};
